import {
  cn,
  getBackgroundStyles,
  getForegroundStyles,
  getFocusRingStyles,
  panelBorderStyles,
  getPanelHoverStyles,
  isPanelColor,
} from "../../styles";
import React, { useRef, forwardRef, useImperativeHandle } from "react";
import {
  BaseElementProps,
  RenderProp,
  ThemeColor,
  PanelColor,
} from "../../props";

/**
 * Visual style variants for the Button component.
 */
export enum ButtonVariant {
  /** Solid background button, typically used for primary actions. */
  contained = "contained",

  /** Button with border and transparent background. */
  outlined = "outlined",

  /** Text-only button with no border or background. Also use this for icon only Buttons. */
  text = "text",

  /** Button that stretches to fill the full width of its container. */
  full = "full",
}

export interface ButtonBaseRenderProps {
  ref: React.Ref<any>;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export type ButtonBaseRender = (
  props: ButtonBaseRenderProps
) => React.ReactNode;

export interface ButtonBaseProps
  extends BaseElementProps,
    Omit<React.HTMLAttributes<HTMLElement>, "color"> {
  /**
   * Custom render function for the button element.
   * @default undefined (renders as button element)
   */
  render?: ButtonBaseRender;

  /**
   * Main content rendered prominently inside the button.
   */
  children?: React.ReactNode;

  /**
   * Button style variant.
   * Should be a value from the imported `ButtonVariant` enum.
   * @default ButtonVariant.contained
   */
  variant?: ButtonVariant;

  /**
   * Button thematic color.
   * Should be a value from the imported `ThemeColor` enum or `PanelColor` enum.
   * @default ThemeColor.primary
   */
  color?: ThemeColor | PanelColor;

  /**
   * Click event handler.
   */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;

  /**
   * Centers the button content horizontally.
   * @default false
   */
  centered?: boolean;

  /**
   * Optional render prop function for custom symbol display.
   * Receives render environment to render a symbol (Icon, Spinner, etc) on the left side of the Button.
   * @default null
   */
  renderSymbol?: RenderProp;

  /**
   * Accessible label for the symbol element.
   * Used for ARIA labeling and tooltip text to provide context for screen readers and assistive technologies.
   */
  symbolLabel?: string;

  /**
   * HTML button type attribute.
   * Defaults to "button" to prevent accidental form submission — use "submit" explicitly for submit buttons.
   * @default "button"
   */
  type?: "button" | "submit" | "reset";
}

/**
 * Flexible and customizable base Button component.
 * Supports custom render functions, multiple visual variants,
 * theme colors, symbols, and states like disabled, muted, or busy.
 * Accepts additional HTML attributes which are forwarded to the rendered element.
 */
export const ButtonBase = forwardRef<any, ButtonBaseProps>(
  (
    {
      render,
      variant = ButtonVariant.contained,
      color = ThemeColor.gray,
      className = "",
      children,
      onClick,
      renderSymbol = null,
      symbolLabel = "",
      disabled = false,
      muted = false,
      centered = false,
      dense = false,
      loading = false,
      ...props
    },
    forwardedRef
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(forwardedRef, () => buttonRef.current!);

    const handleClick = (e) => {
      if (!buttonRef.current) return;
      if (disabled) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // Remove all existing ripples before creating new one
      document.querySelectorAll(".ripple").forEach((ripple) => ripple.remove());

      // Create ripple effect
      const button = e.currentTarget;
      const rect = button.getBoundingClientRect();
      const ripple = document.createElement("span");
      const diameter = Math.max(button.clientWidth, button.clientHeight);
      const radius = diameter / 2;

      ripple.style.width = ripple.style.height = `${diameter}px`;
      ripple.style.left = `${e.clientX - rect.left - radius}px`;
      ripple.style.top = `${e.clientY - rect.top - radius}px`;
      ripple.classList.add("ripple");

      if (variant === "outlined") {
        ripple.classList.add("ripple-outline");
      } else {
        ripple.classList.add("ripple-filled");
      }

      button.appendChild(ripple);

      if (onClick) onClick(e);
    };

    const baseStyles =
      "relative overflow-hidden font-medium inline-flex items-center";

    const variantStyles = {
      contained: "rounded-md",
      outlined: dense ? "rounded-md border-1" : "rounded-md border-2",
      text: "rounded-md",
      full: "w-full",
    };

    const colorStyles: Record<ThemeColor, Record<ButtonVariant, string>> = {
      primary: {
        contained:
          "bg-primary-500 text-front-50 hover:bg-primary-600 dark:bg-primary-600 dark:hover:bg-primary-700",
        outlined:
          "border-primary-500 text-primary-500 hover:bg-primary-100 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-800/30",
        text: "text-primary-500 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-800/30",
        full: "text-primary-500 hover:bg-primary-100 dark:text-primary-400 dark:hover:bg-primary-800/30",
      },
      secondary: {
        contained:
          "bg-secondary-500 text-front-50 hover:bg-secondary-600 dark:bg-secondary-600 dark:hover:bg-secondary-700",
        outlined:
          "border-secondary-500 text-secondary-500 hover:bg-secondary-100 dark:border-secondary-400 dark:text-secondary-400 dark:hover:bg-secondary-800/30",
        text: "text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800/30",
        full: "text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800/30",
      },
      gray: {
        contained:
          "text-front-50 bg-back-400 hover:bg-back-500 dark:bg-back-600 dark:hover:bg-back-700",
        outlined:
          "border-back-400 text-front-600 hover:bg-back-200 dark:border-back-600 dark:text-front-300 dark:hover:bg-back-600",
        text: "text-front-600 hover:bg-back-200 dark:text-front-300 dark:hover:bg-back-600",
        full: "text-front-600 hover:bg-back-200 dark:text-front-300 dark:hover:bg-back-600",
      },
      error: {
        contained:
          "bg-error-600 text-front-50 hover:bg-error-700 dark:bg-error-700 dark:hover:bg-error-800",
        outlined:
          "border-error-500 text-error-500 hover:bg-error-100 dark:border-error-400 dark:text-error-400 dark:hover:bg-error-800/30",
        text: "text-error-500 hover:bg-error-100 dark:text-error-400 dark:hover:bg-error-800/30",
        full: "text-error-500 hover:bg-error-100 dark:text-error-400 dark:hover:bg-error-800/30",
      },
      warning: {
        contained:
          "bg-warning-600 text-front-50 hover:bg-warning-700 dark:bg-warning-700 dark:hover:bg-warning-800",
        outlined:
          "border-warning-500 text-warning-500 hover:bg-warning-100 dark:border-warning-400 dark:text-warning-400 dark:hover:bg-warning-800/30",
        text: "text-warning-500 hover:bg-warning-100 dark:text-warning-400 dark:hover:bg-warning-800/30",
        full: "text-warning-500 hover:bg-warning-100 dark:text-warning-400 dark:hover:bg-warning-800/30",
      },
      success: {
        contained:
          "bg-success-600 text-front-50 hover:bg-success-700 dark:bg-success-700 dark:hover:bg-success-800",
        outlined:
          "border-success-500 text-success-500 hover:bg-success-100 dark:border-success-400 dark:text-success-400 dark:hover:bg-success-800/30",
        text: "text-success-500 hover:bg-success-100 dark:text-success-400 dark:hover:bg-success-800/30",
        full: "text-success-500 hover:bg-success-100 dark:text-success-400 dark:hover:bg-success-800/30",
      },
      transparent: {
        contained:
          "text-front-800 bg-front-200/20 hover:bg-front-200/40 dark:text-front-100 dark:bg-front-700/20 dark:hover:bg-front-700/40",
        outlined:
          "border-front-200/50 text-front-800 hover:bg-front-200/30 dark:border-front-700/50 dark:text-front-100 dark:hover:bg-front-700/30",
        text: "text-front-800 hover:bg-front-200/30 dark:text-front-100 dark:hover:bg-front-700/30",
        full: "text-front-800 hover:bg-front-200/30 dark:text-front-100 dark:hover:bg-front-700/30",
      },
      none: {
        contained: "",
        outlined: "",
        text: "",
        full: "",
      },
      danger: {
        contained: "",
        outlined: "",
        text: "",
        full: "",
      },
      alert: {
        contained: "",
        outlined: "",
        text: "",
        full: "",
      },
      done: {
        contained: "",
        outlined: "",
        text: "",
        full: "",
      },
    };
    // colorStyles.normal now has its own distinct styling
    colorStyles.danger = colorStyles.error;
    colorStyles.alert = colorStyles.warning;
    colorStyles.done = colorStyles.success;

    const disabledStyles = disabled
      ? "opacity-30 cursor-not-allowed"
      : "cursor-pointer";

    const mutedStyles = muted && !disabled ? "opacity-75" : "";

    const symbolSizeClassName = children
      ? dense
        ? "w-3 h-3"
        : "w-4 h-4"
      : dense
        ? "w-4 h-4"
        : "w-5 h-5";

    const colorClassName = isPanelColor(color)
      ? variant === "text" || variant === "full"
        ? getPanelHoverStyles(color as PanelColor)
        : variant === "outlined"
          ? cn(
              `border-2 ${panelBorderStyles[color as PanelColor]}`,
              getPanelHoverStyles(color as PanelColor)
            )
          : cn(getBackgroundStyles(color), getForegroundStyles(color))
      : colorStyles[color as ThemeColor][variant];

    const finalClassName = cn(
      baseStyles,
      "outline-none focus-visible:ring-2",
      getFocusRingStyles(ThemeColor.primary),
      renderSymbol && !children
        ? dense
          ? variant === "outlined"
            ? "p-1.5"
            : "p-[7px]"
          : variant === "outlined"
            ? "p-1.5"
            : "p-2"
        : dense
          ? variant === "outlined"
            ? "px-2 py-1.5"
            : "px-3 py-[7px]"
          : variant === "outlined"
            ? "px-3 py-1.5"
            : "px-4 py-2",
      dense ? "text-xs font-normal gap-1" : "text-sm gap-2",
      colorClassName,
      variantStyles[variant],
      disabledStyles,
      mutedStyles,
      centered && "justify-center",
      className
    );

    const buttonContent = (
      <>
        {renderSymbol &&
          renderSymbol({
            sizeClassName: symbolSizeClassName,
            loading,
            color,
            dense,
            muted,
            disabled,
          })}
        {children}
      </>
    );

    if (render) {
      return render({
        ref: buttonRef,
        onClick: handleClick,
        disabled,
        className: finalClassName,
        children: buttonContent,
        ...props,
      });
    }

    return (
      <button
        ref={buttonRef}
        type="button"
        onClick={handleClick}
        disabled={disabled}
        className={finalClassName}
        {...props}
      >
        {buttonContent}
      </button>
    );
  }
);
